import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, EyeOff, Flag, MessageSquarePlus, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime, relTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmptyState, FilterSelect, Loading, PageHeader, Pill, Section, severityTone, Spinner } from "@/components/common";
import { RecordingCompliance } from "@/components/RecordingCompliance";
import { BulkActionBar, CleanupDialog } from "@/components/AnomalyBulk";
import { cn } from "@/lib/utils";

const TYPE_LABEL = { repeated_number: "Repeated number", short_call_burst: "Short-call burst", after_hours: "After hours", volume_spike: "Volume spike", score_drop: "Score drop", unrecorded_calls: "Unrecorded calls" };
const TYPE_OPTIONS = Object.entries(TYPE_LABEL).map(([v, l]) => ({ value: v, label: l }));

function AnomalyCard({ a, selected, onToggle }) {
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const m = useMutation({
    mutationFn: (body) => api.patch(`/anomalies/${a.id}`, body).then((r) => r.data),
    onSuccess: () => { toast.success("Anomaly updated"); setNote(""); qc.invalidateQueries({ queryKey: ["anomalies"] }); qc.invalidateQueries({ queryKey: ["recent"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const stripe = a.severity === "high" ? "border-l-red-500" : a.severity === "medium" ? "border-l-amber-500" : "border-l-blue-500";
  return (
    <div className={cn("panel panel-hover p-4 border-l-2 fade-up", stripe, a.status !== "open" && "opacity-70", selected && "ring-1 ring-blue-500/60")} data-testid={`anomaly-card-${a.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <Checkbox data-testid={`anomaly-select-${a.id}`} checked={selected} onCheckedChange={() => onToggle(a.id)} className="mr-1" />
          <Pill tone={severityTone(a.severity)}>{a.severity}</Pill>
          <Pill tone="violet">{TYPE_LABEL[a.type] || a.type}</Pill>
          <Pill tone={a.status === "open" ? "amber" : a.status === "escalated" ? "red" : "slate"}>{a.status}</Pill>
          {a.notified && <Pill tone="blue">admins notified</Pill>}
        </div>
        <span className="text-[11px] font-mono text-slate-500">detected {relTime(a.detected_at)}{a.last_seen_at !== a.detected_at ? ` · seen ${relTime(a.last_seen_at)}` : ""}</span>
      </div>
      <h3 className="text-base font-bold text-slate-100 mt-2">{a.agent_user_id ? <Link to={`/agents/${a.agent_user_id}`} className="hover:text-blue-300" data-testid={`anomaly-agent-link-${a.id}`}>{a.title}</Link> : a.title}</h3>
      <p className="text-sm text-slate-400 mt-1 leading-relaxed">{a.description}</p>
      {a.details && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
          {Object.entries(a.details).filter(([, v]) => typeof v !== "object").map(([k, v]) => (
            <div key={k} className="bg-[#0f172a] border border-[#1f293d] rounded p-2">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{k.replace(/_/g, " ")}</div>
              <div className="font-mono text-sm text-slate-200 truncate">{String(v)}</div>
            </div>
          ))}
        </div>
      )}
      {a.notes?.length > 0 && (
        <ul className="mt-3 space-y-1">
          {a.notes.map((n, i) => <li key={i} className="text-xs text-slate-300 pl-2 border-l border-[#2d3d58]"><span className="text-slate-500 font-mono">{fmtDateTime(n.at)} · {n.by}:</span> {n.text}</li>)}
        </ul>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Input data-testid={`anomaly-note-input-${a.id}`} placeholder="Add an investigation note…" value={note} onChange={(e) => setNote(e.target.value)} className="h-8 text-xs bg-[#0f172a] border-[#1f293d] flex-1 min-w-[180px]" />
        <Button data-testid={`anomaly-add-note-${a.id}`} size="sm" variant="outline" className="h-8 text-xs border-[#2d3d58] bg-[#0f172a]" disabled={!note.trim() || m.isPending} onClick={() => m.mutate({ note })}><MessageSquarePlus className="h-3.5 w-3.5 mr-1" /> Note</Button>
        {a.status !== "reviewed" && <Button data-testid={`anomaly-review-${a.id}`} size="sm" variant="outline" className="h-8 text-xs border-emerald-500/30 text-emerald-300 bg-[#0f172a] hover:bg-emerald-500/10" disabled={m.isPending} onClick={() => m.mutate({ status: "reviewed" })}><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Reviewed</Button>}
        {a.status !== "escalated" && <Button data-testid={`anomaly-escalate-${a.id}`} size="sm" variant="outline" className="h-8 text-xs border-red-500/30 text-red-300 bg-[#0f172a] hover:bg-red-500/10" disabled={m.isPending} onClick={() => m.mutate({ status: "escalated" })}><Flag className="h-3.5 w-3.5 mr-1" /> Escalate</Button>}
        {a.status !== "dismissed" && <Button data-testid={`anomaly-dismiss-${a.id}`} size="sm" variant="ghost" className="h-8 text-xs text-slate-400" disabled={m.isPending} onClick={() => m.mutate({ status: "dismissed" })}><EyeOff className="h-3.5 w-3.5 mr-1" /> Dismiss</Button>}
        {a.status !== "open" && <Button data-testid={`anomaly-reopen-${a.id}`} size="sm" variant="ghost" className="h-8 text-xs text-slate-400" disabled={m.isPending} onClick={() => m.mutate({ status: "open" })}>Reopen</Button>}
      </div>
    </div>
  );
}

function HeatMatrix() {
  const q = useQuery({ queryKey: ["agent-matrix"], queryFn: () => api.get("/anomalies/agent-matrix", { params: { days: 14 } }).then((r) => r.data) });
  if (q.isLoading) return <Loading />;
  const rows = q.data?.matrix || [];
  if (!rows.length) return <EmptyState title="No activity yet" />;
  const max = Math.max(1, ...rows.flatMap((r) => r.hours));
  return (
    <div className="overflow-x-auto" data-testid="agent-heat-matrix">
      <table className="text-[10px] font-mono">
        <thead>
          <tr><th className="text-left text-slate-500 pr-3 pb-1">Agent</th>{Array.from({ length: 24 }, (_, h) => <th key={h} className="text-slate-600 px-[1px] pb-1 font-normal">{h % 3 === 0 ? h : ""}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.agent}>
              <td className="text-slate-300 pr-3 whitespace-nowrap py-[1px]">{r.agent}</td>
              {r.hours.map((v, h) => (
                <td key={h} className="px-[1px]"><div title={`${r.agent} · ${h}:00 · ${v} calls`} className="h-4 w-4 rounded-[2px]" style={{ background: v ? `rgba(59,130,246,${0.15 + 0.85 * (v / max)})` : "#0f172a", border: "1px solid #1f293d" }} /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="text-[10px] text-slate-500 mt-2">Calls per hour of day (local time), last 14 days. Bright cells outside business hours or at odd hours warrant a look.</div>
    </div>
  );
}

export default function Anomalies() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("open");
  const [severity, setSeverity] = useState("");
  const [type, setType] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const q = useQuery({ queryKey: ["anomalies", status, severity, type], queryFn: () => api.get("/anomalies", { params: { status, severity: severity || undefined, type: type || undefined } }).then((r) => r.data) });
  const run = useMutation({ mutationFn: () => api.post("/jobs/anomalies").then((r) => r.data), onSuccess: (d) => { toast.success(`Detection complete · ${d.new_anomalies} new anomal${d.new_anomalies === 1 ? "y" : "ies"}`); qc.invalidateQueries({ queryKey: ["anomalies"] }); }, onError: (e) => toast.error(errMsg(e)) });
  const counts = q.data?.counts || {};
  const items = q.data?.items || [];
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allShown = items.length > 0 && items.every((a) => selected.has(a.id));
  const toggleAll = () => setSelected(allShown ? new Set() : new Set(items.map((a) => a.id)));
  const clear = () => setSelected(new Set());

  return (
    <>
      <PageHeader
        eyebrow="Pattern anomaly detector"
        title="Unusual activity"
        description="Hourly scans for repeated calls to the same number (possible fake busy-work), bursts of very short calls, after-hours dialing, volume spikes, week-over-week score drops and calls that were not recorded. High-severity findings email + Slack the admins."
        actions={
          <>
            <CleanupDialog typeOptions={TYPE_OPTIONS} />
            <Button data-testid="anomalies-run-button" size="sm" variant="outline" className="h-8 text-xs border-[#2d3d58] bg-[#0f172a] hover:bg-[#161f30]" disabled={run.isPending} onClick={() => run.mutate()}>
              {run.isPending ? <Spinner /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />} Run detection now
            </Button>
          </>
        }
        testId="anomalies-header"
      />

      <div className="flex flex-wrap items-center gap-2 fade-up">
        <Tabs value={status} onValueChange={(v) => { setStatus(v); clear(); }}>
          <TabsList className="bg-[#111827] border border-[#1f293d] h-9" data-testid="anomalies-status-tabs">
            <TabsTrigger value="open" data-testid="anomalies-tab-open" className="text-xs">Open <span className="ml-1.5 font-mono text-amber-300">{counts.open || 0}</span></TabsTrigger>
            <TabsTrigger value="escalated" data-testid="anomalies-tab-escalated" className="text-xs">Escalated <span className="ml-1.5 font-mono text-red-300">{counts.escalated || 0}</span></TabsTrigger>
            <TabsTrigger value="reviewed" data-testid="anomalies-tab-reviewed" className="text-xs">Reviewed <span className="ml-1.5 font-mono text-slate-400">{counts.reviewed || 0}</span></TabsTrigger>
            <TabsTrigger value="dismissed" data-testid="anomalies-tab-dismissed" className="text-xs">Dismissed</TabsTrigger>
            <TabsTrigger value="all" data-testid="anomalies-tab-all" className="text-xs">All</TabsTrigger>
          </TabsList>
        </Tabs>
        <FilterSelect testId="anomalies-filter-severity" placeholder="Severities" value={severity} onChange={(v) => { setSeverity(v); clear(); }} options={["high", "medium", "low"].map((s) => ({ value: s, label: s }))} />
        <FilterSelect testId="anomalies-filter-type" placeholder="Types" value={type} onChange={(v) => { setType(v); clear(); }} options={TYPE_OPTIONS} />
        {items.length > 0 && (
          <label className="ml-auto flex items-center gap-2 text-xs text-slate-400 cursor-pointer" data-testid="anomalies-select-all-label">
            <Checkbox data-testid="anomalies-select-all" checked={allShown} onCheckedChange={toggleAll} /> Select all shown ({items.length})
          </label>
        )}
      </div>

      <BulkActionBar selected={selected} onClear={clear} />

      <div className="grid xl:grid-cols-[1fr_420px] gap-4 items-start">
        <div className="space-y-3" data-testid="anomalies-list">
          {q.isLoading ? <Loading /> : items.length ? items.map((a) => <AnomalyCard key={a.id} a={a} selected={selected.has(a.id)} onToggle={toggle} />) : (
            <Section><EmptyState icon={status === "open" ? ShieldCheck : AlertTriangle} title={status === "open" ? "No open anomalies" : "Nothing here"} hint="Thresholds (repeat count, short-call seconds, after-hours) are adjustable in Settings." testId="anomalies-empty" /></Section>
          )}
        </div>
        <div className="space-y-4">
          <RecordingCompliance />
          <Section title="Agent activity heat matrix" eyebrow="Hour-of-day pattern" testId="heat-matrix-section"><HeatMatrix /></Section>
        </div>
      </div>
    </>
  );
}
